import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { MapPin, Check, Loader2, ExternalLink } from 'lucide-react';
import { API } from '@/App';

/**
 * Compact inline helper: shows a "📍 Pin" button next to any location field.
 * Click opens a popover with a Google Maps URL input. On save, the backend
 * upserts a `pollen_locations` row matched on (name, type) and writes the
 * extracted coords as a verified pin.
 *
 * `type` defaults to 'city' since this button is placed next to overnight /
 * destination / hotel-city fields in the tour editor. Pass 'hotel', 'guide',
 * etc. for other contexts.
 * Does not alter the tour being edited; purely updates the shared Locations DB.
 *
 * If a `currentUrl` (or `currentLat`+`currentLng`) is passed in, an extra
 * "Open in Maps" link is rendered next to the Pin button so the user can
 * jump straight into Google Maps for that record.
 */
const LocationPinBtn = ({ locationName, type = 'city', city, size = 'sm', onPinned, currentUrl, currentLat, currentLng }) => {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const name = (locationName || '').trim();
    if (!name) { toast.error('Enter the location name first.'); return; }
    if (!url.trim()) return;
    setBusy(true);
    try {
      const t = localStorage.getItem('session_token');
      const res = await fetch(`${API}/pollen/locations/pin-by-name`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) },
        body: JSON.stringify({ name, type, url: url.trim(), city: city || '' }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Could not pin');
      toast.success(`${name} pinned ✓  (${data.lat.toFixed(4)}, ${data.lng.toFixed(4)})`);
      setUrl(''); setOpen(false);
      onPinned && onPinned(data);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  // Always resolve Open to an absolute https://maps URL so the link never
  // gets interpreted as an internal route.
  const isAbsoluteUrl = /^https?:\/\//i.test((currentUrl || '').trim());
  const openUrl = isAbsoluteUrl
    ? currentUrl.trim()
    : (currentLat != null && currentLng != null
        ? `https://www.google.com/maps?q=${currentLat},${currentLng}`
        : null);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <span className="inline-flex items-center gap-1">
      {openUrl && (
        <a
          href={openUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="h-7 px-1.5 text-[10px] inline-flex items-center rounded-md text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50"
          title="Open this pin in Google Maps"
          data-testid="location-open-map-btn"
          onClick={(e) => e.stopPropagation()}
        >
          <ExternalLink className="w-3 h-3 mr-0.5" />Open
        </a>
      )}
      <PopoverTrigger asChild>
        <Button type="button" variant="ghost" size={size} className="h-7 px-1.5 text-[10px] text-muted-foreground hover:text-primary" title="Pin with Google Maps URL" data-testid="location-pin-btn">
          <MapPin className="w-3 h-3 mr-0.5" />Pin
        </Button>
      </PopoverTrigger>
      </span>
      <PopoverContent side="bottom" align="end" className="w-80 p-3 space-y-2">
        <div className="text-[11px] text-muted-foreground">
          Pin <span className="font-medium text-foreground">{locationName || '(no name)'}</span> in the shared Locations DB.
        </div>
        <Input
          autoFocus
          placeholder='Google Maps URL or "lat, lng"'
          value={url}
          onChange={e => setUrl(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') submit(); }}
          className="h-8 text-xs"
          data-testid="location-pin-url-input"
        />
        <div className="flex items-center gap-1">
          <Button size="sm" onClick={submit} disabled={busy || !url.trim()} className="h-7 text-xs flex-1" data-testid="location-pin-save">
            {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <><Check className="w-3 h-3 mr-1" />Save</>}
          </Button>
          <Button type="button" variant="outline" size="sm" className="h-7 text-xs" onClick={() => setOpen(false)}>Cancel</Button>
        </div>
        <p className="text-[10px] text-muted-foreground">Also works with short URLs like <code>share.google/…</code> or <code>maps.app.goo.gl/…</code>.</p>
      </PopoverContent>
    </Popover>
  );
};

export default LocationPinBtn;
